import { BulkResolveWithValueDialog } from "./BulkResolveWithValueDialog";
import { type Discrepancy } from "./DiscrepancyReviewDialog";
import { type ActionKey } from "./DiscrepancyReviewDialog.helpers";
import { ResolveDiscrepanciesDialog, type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import { ResolveWithValueDialog } from "./ResolveWithValueDialog";

export interface ResolveTarget {
  rows: Discrepancy[];
  includeVendorAction: boolean;
  clearSelection: () => void;
}

export interface ResolveDialogSwitchProps {
  resolveTarget: ResolveTarget | null;
  options: ResolveActionOption[];
  isStaff: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (action: ActionKey, eligibleIds: string[], note: string | null) => void;
  notifyMutated: () => void;
  onViewDocument: (documentId: string, fileExtension: string | null | undefined) => void;
}

/**
 * Picks the two-question value flow for a staff-resolved Data Source
 * Conflict, and the single-radio flow for everything else
 * (resolveDiscrepancies is staff-only server-side).
 */
export function ResolveDialogSwitch({
  resolveTarget,
  options,
  isStaff,
  loading,
  onClose,
  onConfirm,
  notifyMutated,
  onViewDocument,
}: ResolveDialogSwitchProps) {
  const open = resolveTarget !== null;
  const rows = resolveTarget?.rows ?? [];
  const useValueFlow = isStaff && (resolveTarget?.includeVendorAction ?? false);

  if (useValueFlow) {
    const dismissOption = options.find((o) => o.action === "nonDiscrepancy");
    const cantDetermineOption = options.find((o) => o.action === "cantDetermine");
    // nonDiscrepancy is always present once options are computed, but this
    // guard keeps the component safe to call with an empty options list
    // before a target is set.
    if (!dismissOption) return null;
    // A bulk selection has no single row to prefill from — its own dialog
    // asks one question ("what's correct for all N?") instead of the
    // single-row two-question flow.
    if (rows.length > 1) {
      return (
        <BulkResolveWithValueDialog
          open={open}
          rows={rows}
          clearSelection={resolveTarget?.clearSelection ?? (() => {})}
          dismissOption={dismissOption}
          cantDetermineOption={cantDetermineOption}
          otherMutationLoading={loading}
          onClose={onClose}
          onConfirm={onConfirm}
          notifyMutated={notifyMutated}
        />
      );
    }
    return (
      <ResolveWithValueDialog
        open={open}
        rows={rows}
        clearSelection={resolveTarget?.clearSelection ?? (() => {})}
        singleDiscrepancyId={rows.length === 1 ? rows[0].id : undefined}
        isStaff={isStaff}
        dismissOption={dismissOption}
        cantDetermineOption={cantDetermineOption}
        otherMutationLoading={loading}
        onClose={onClose}
        onConfirm={onConfirm}
        notifyMutated={notifyMutated}
        onViewDocument={onViewDocument}
      />
    );
  }

  return (
    <ResolveDiscrepanciesDialog
      open={open}
      totalCount={rows.length}
      options={options}
      loading={loading}
      singleDiscrepancyId={rows.length === 1 ? rows[0].id : undefined}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}
